import React, { useState, useEffect, useCallback } from "react";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Cookies from "js-cookie";

import { Card, Button, Alert } from "tabler-react";

function TelegramConnectButton({ t, onConnectionStart, isConnecting }) {
    // State for the component
    const [isLoading, setIsLoading] = useState(false);
    const [telegramUrl, setTelegramUrl] = useState(null);
    const [expiresAt, setExpiresAt] = useState(null);
    const [error, setError] = useState(null);

    const fetchTelegramLink = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            // Call API to generate connection link
            const response = await fetch("/d/api/telegram/generate-link/", {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": Cookies.get("csrftoken"),
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(
                    errorData.error ||
                        t("shop.account.telegram.error_generating_link"),
                );
            }

            const data = await response.json();
            setTelegramUrl(data.telegram_url);

            // Calculate expiration time
            if (data.expires_in) {
                const expirationTime = new Date(
                    Date.now() + data.expires_in * 1000,
                );
                setExpiresAt(expirationTime);
            }

            // We don't call onConnectionStart here - only when user clicks the link
        } catch (error) {
            console.error("Error generating telegram link:", error);
            setError(
                error.message ||
                    t("shop.account.telegram.error_generating_link"),
            );
            toast.error(
                error.message ||
                    t("shop.account.telegram.error_generating_link"),
                {
                    position: toast.POSITION.BOTTOM_RIGHT,
                },
            );
        } finally {
            setIsLoading(false);
        }
    }, [t]);

    // Fetch the link when component mounts, but don't trigger onConnectionStart
    // We'll only start the connection process when the user clicks the button
    useEffect(() => {
        fetchTelegramLink();
    }, [fetchTelegramLink]);

    // Handle manual refresh of the link
    const handleRefresh = () => {
        fetchTelegramLink();
    };

    if (error) {
        return (
            <Card>
                <Card.Status color="danger" />
                <Card.Header>
                    <Card.Title>{t("shop.account.telegram.title")}</Card.Title>
                </Card.Header>
                <Card.Body>
                    <Alert type="danger" icon="alert-triangle" className="mb-3">
                        {error}
                    </Alert>

                    <div className="mt-4">
                        <h5>
                            {t("shop.account.telegram.troubleshooting_title")}
                        </h5>
                        <ul className="pl-3">
                            <li>
                                {t(
                                    "shop.account.telegram.troubleshooting_step_1",
                                )}
                            </li>
                            <li>
                                {t(
                                    "shop.account.telegram.troubleshooting_step_2",
                                )}
                            </li>
                        </ul>
                    </div>

                    <div className="d-flex justify-content-end mt-4">
                        <Button
                            color="danger"
                            onClick={handleRefresh}
                            disabled={isLoading}
                            icon="refresh"
                        >
                            {t("shop.account.telegram.try_again")}
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    if (isLoading) {
        return (
            <Card>
                <Card.Status color="primary" />
                <Card.Header>
                    <Card.Title>{t("shop.account.telegram.title")}</Card.Title>
                </Card.Header>
                <Card.Body className="text-center p-4">
                    <div className="mb-3">
                        <div className="loader"></div>
                    </div>
                    <h4>{t("shop.account.telegram.generating_link")}</h4>
                    <p className="text-muted mb-0">
                        {t("shop.account.telegram.please_wait")}
                    </p>
                </Card.Body>
            </Card>
        );
    }

    if (telegramUrl) {
        return (
            <Card>
                <Card.Status color="primary" />
                <Card.Header>
                    <Card.Title>{t("shop.account.telegram.title")}</Card.Title>
                </Card.Header>
                <Card.Body>
                    <p>{t("shop.account.telegram.click_link_to_connect")}</p>

                    <div className="mb-4">
                        <h5>{t("shop.account.telegram.instructions_title")}</h5>
                        <ol className="pl-3">
                            <li>
                                {t("shop.account.telegram.instruction_step_1")}
                            </li>
                            <li>
                                {t("shop.account.telegram.instruction_step_2")}
                            </li>
                            <li>
                                {t("shop.account.telegram.instruction_step_3")}
                            </li>
                        </ol>
                    </div>

                    <Alert type="info" icon="info-circle" className="mb-4">
                        {expiresAt
                            ? t("shop.account.telegram.link_expires_at", {
                                  time: expiresAt.toLocaleTimeString(),
                                  date: expiresAt.toLocaleDateString(),
                              })
                            : t("shop.account.telegram.link_valid_for_30_min")}
                    </Alert>

                    <div className="text-center mb-4">
                        <a
                            href={telegramUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-primary btn-lg btn-block"
                            onClick={(e) => {
                                // Notify parent component that connection process started
                                if (onConnectionStart) {
                                    onConnectionStart();
                                }

                                // Display toast notification
                                toast.info(
                                    t("shop.account.telegram.link_opened"),
                                    {
                                        position: toast.POSITION.BOTTOM_RIGHT,
                                        autoClose: 5000,
                                    },
                                );

                                // Default link behavior continues normally
                                // No need for e.preventDefault()
                            }}
                        >
                            <i className="fe fe-message-circle mr-2"></i>
                            {t("shop.account.telegram.open_telegram")}
                        </a>
                    </div>

                    <div className="d-flex justify-content-end">
                        <Button
                            color="secondary"
                            size="sm"
                            icon="refresh"
                            onClick={handleRefresh}
                        >
                            {t("shop.account.telegram.refresh_link")}
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    // This is the default initial state - show benefits and connect button
    return (
        <Card>
            <Card.Status color="secondary" />
            <Card.Header>
                <Card.Title>{t("shop.account.telegram.title")}</Card.Title>
            </Card.Header>
            <Card.Body className="p-4">
                <p className="mb-3">{t("shop.account.telegram.description")}</p>

                <div className="mb-4">
                    <h5>{t("shop.account.telegram.other_benefits_coming")}</h5>
                    <ul className="pl-3">
                        <li>
                            {t(
                                "shop.account.telegram.benefit_booking_confirmations",
                            )}
                        </li>
                        <li>
                            {t(
                                "shop.account.telegram.benefit_waitlist_updates",
                            )}
                        </li>
                    </ul>
                </div>

                <div className="text-center">
                    <Button
                        color="primary"
                        size="lg"
                        icon="message-circle"
                        block
                        loading={isLoading}
                        disabled={isLoading}
                        onClick={fetchTelegramLink}
                        className="mt-2"
                    >
                        {t("shop.account.telegram.connect_button")}
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
}

export default withTranslation()(TelegramConnectButton);
